/**
 * NVIDIA InfiniBand Switch Database
 *
 * Complete specifications for NVIDIA InfiniBand switches including
 * Quantum-X800 (XDR), Quantum-2 (NDR), and Quantum HDR series.
 *
 * Data sources: research/switch-specs.md
 */

import type { SwitchModel } from '../../types/switches';

/**
 * Quantum-X800 Q3400-RA (4U, 144-port XDR switch)
 */
export const QUANTUM_X800_Q3400: SwitchModel = {
  id: 'nvidia-qx800-q3400-ra',
  name: 'Q3400-RA',
  vendor: 'NVIDIA',
  productName: 'Quantum-X800 Q3400-RA',
  fabricType: 'InfiniBand',
  typicalRoles: ['leaf', 'spine'],
  ports: {
    totalPorts: 144,
    speed: {
      value: 800,
      unit: 'Gbps',
      displayName: '800G XDR',
    },
    portType: 'OSFP',
    supportsBreakout: false,
  },
  power: {
    typicalWatts: 1200, // Estimate - CPO variant significantly better
    maxWatts: 1800, // Estimate - pending official datasheet
    idleWatts: 400,
    perPortWatts: 8,
    psuConfig: {
      count: 2,
      capacity: 2000,
      redundancy: 'N+1',
    },
  },
  physical: {
    rackUnits: 4,
    widthMm: 438,
    depthMm: 660,
    weightKg: 25,
    airflow: 'front-to-back',
    maxBtuPerHour: 6140,
  },
  performance: {
    switchingCapacityTbps: 115.2,
    forwardingRateMpps: 85000,
    latencyNs: 100, // Ultra-low latency estimate
    bufferSizeMB: 256,
  },
  features: {
    layer2: true,
    layer3: true,
    vxlan: false,
    evpn: false,
    rdma: true,
    adaptiveRouting: true,
    telemetry: true,
    inBandManagement: true,
    oobManagement: true,
    additionalFeatures: [
      'SHARP v4 in-network computing',
      'Telemetry-based congestion control',
      'Performance isolation',
      'CPO variant available',
    ],
  },
  metadata: {
    productUrl: 'https://www.nvidia.com/en-us/networking/products/infiniband/quantum-x800/',
    releaseYear: 2025,
    endOfLife: false,
    notes: '72 OSFP cages (2 ports per cage). CPO variant reduces power by 63x and insertion loss from ~22dB to ~4dB.',
  },
};

/**
 * Quantum-X800 Q3200-RA (2U dual switch, 72-port total)
 */
export const QUANTUM_X800_Q3200: SwitchModel = {
  id: 'nvidia-qx800-q3200-ra',
  name: 'Q3200-RA',
  vendor: 'NVIDIA',
  productName: 'Quantum-X800 Q3200-RA',
  fabricType: 'InfiniBand',
  typicalRoles: ['leaf', 'spine'],
  ports: {
    totalPorts: 72, // 2 independent switches with 36 ports each
    speed: {
      value: 800,
      unit: 'Gbps',
      displayName: '800G XDR',
    },
    portType: 'OSFP',
    supportsBreakout: false,
  },
  power: {
    typicalWatts: 800,
    maxWatts: 1200,
    idleWatts: 300,
    perPortWatts: 8,
    psuConfig: {
      count: 2,
      capacity: 1500,
      redundancy: 'N+1',
    },
  },
  physical: {
    rackUnits: 2,
    widthMm: 438,
    depthMm: 660,
    weightKg: 18,
    airflow: 'front-to-back',
    maxBtuPerHour: 4095,
  },
  performance: {
    switchingCapacityTbps: 57.6,
    forwardingRateMpps: 42500,
    latencyNs: 100,
    bufferSizeMB: 128,
  },
  features: {
    layer2: true,
    layer3: true,
    vxlan: false,
    evpn: false,
    rdma: true,
    adaptiveRouting: true,
    telemetry: true,
    inBandManagement: true,
    oobManagement: true,
    additionalFeatures: ['SHARP v4', 'Space-efficient dual switch design'],
  },
  metadata: {
    productUrl: 'https://www.nvidia.com/en-us/networking/products/infiniband/quantum-x800/',
    releaseYear: 2025,
    endOfLife: false,
    notes: 'Space-efficient 2U design with 2 independent 36-port switches for medium scale deployments.',
  },
};

/**
 * Quantum-X800 Q3450-LD (Direct MPO, 144-port)
 */
export const QUANTUM_X800_Q3450: SwitchModel = {
  id: 'nvidia-qx800-q3450-ld',
  name: 'Q3450-LD',
  vendor: 'NVIDIA',
  productName: 'Quantum-X800 Q3450-LD',
  fabricType: 'InfiniBand',
  typicalRoles: ['leaf', 'spine'],
  ports: {
    totalPorts: 144,
    speed: {
      value: 800,
      unit: 'Gbps',
      displayName: '800G XDR',
    },
    portType: 'OSFP',
    supportsBreakout: false,
  },
  power: {
    typicalWatts: 900, // Lower than Q3400 due to no external optics
    maxWatts: 1500,
    idleWatts: 350,
    perPortWatts: 5,
    psuConfig: {
      count: 2,
      capacity: 1800,
      redundancy: 'N+1',
    },
  },
  physical: {
    rackUnits: 4,
    widthMm: 438,
    depthMm: 660,
    weightKg: 24,
    airflow: 'front-to-back',
    maxBtuPerHour: 5115,
  },
  performance: {
    switchingCapacityTbps: 115.2,
    forwardingRateMpps: 85000,
    latencyNs: 90, // Lower latency due to direct MPO
    bufferSizeMB: 256,
  },
  features: {
    layer2: true,
    layer3: true,
    vxlan: false,
    evpn: false,
    rdma: true,
    adaptiveRouting: true,
    telemetry: true,
    inBandManagement: true,
    oobManagement: true,
    additionalFeatures: ['Direct MPO connectivity', 'No external optics required', 'Reduced latency and power'],
  },
  metadata: {
    productUrl: 'https://www.nvidia.com/en-us/networking/products/infiniband/quantum-x800/',
    releaseYear: 2025,
    endOfLife: false,
    notes: 'Direct MPO connectivity reduces latency and power consumption by eliminating external optics.',
  },
};

/**
 * Quantum-2 QM9700 (Managed, 64-port NDR)
 */
export const QUANTUM_2_QM9700: SwitchModel = {
  id: 'nvidia-q2-qm9700',
  name: 'QM9700',
  vendor: 'NVIDIA',
  productName: 'Quantum-2 QM9700',
  fabricType: 'InfiniBand',
  typicalRoles: ['leaf', 'spine'],
  ports: {
    totalPorts: 64,
    speed: {
      value: 400,
      unit: 'Gbps',
      displayName: '400G NDR',
    },
    portType: 'OSFP',
    supportsBreakout: true,
    breakoutOptions: ['1x400G', '2x200G (NDR200)', '4x100G'],
  },
  power: {
    typicalWatts: 747, // Passive cables
    maxWatts: 1720, // Active cables
    idleWatts: 350,
    perPortWatts: 15,
    psuConfig: {
      count: 2,
      capacity: 2000,
      redundancy: 'N+1',
    },
  },
  physical: {
    rackUnits: 1,
    widthMm: 438,
    depthMm: 660,
    weightKg: 14.8, // With dual PSU
    airflow: 'front-to-back',
    maxBtuPerHour: 5867,
  },
  performance: {
    switchingCapacityTbps: 25.6,
    forwardingRateMpps: 66500,
    latencyNs: 130,
    bufferSizeMB: 64,
  },
  features: {
    layer2: true,
    layer3: true,
    vxlan: false,
    evpn: false,
    rdma: true,
    adaptiveRouting: true,
    telemetry: true,
    inBandManagement: true,
    oobManagement: true,
    additionalFeatures: [
      'Onboard subnet manager (up to 2,000 nodes)',
      'SHARP v3 in-network computing',
      'Intel Core i3 Coffee Lake CPU',
      'MLNX-OS',
    ],
  },
  metadata: {
    productUrl: 'https://docs.nvidia.com/networking/display/qm97x0pub/introduction',
    datasheet: 'https://www.delltechnologies.com/asset/en-us/products/networking/technical-support/nvidia-quantum-2-qm9700-series-datasheet.pdf',
    releaseYear: 2022,
    endOfLife: false,
    notes: 'Standard managed switch for H100/H200 DGX systems. 32 OSFP connectors (splittable to 128x200G).',
  },
};

/**
 * Quantum-2 QM9790 (Unmanaged, 64-port NDR)
 */
export const QUANTUM_2_QM9790: SwitchModel = {
  id: 'nvidia-q2-qm9790',
  name: 'QM9790',
  vendor: 'NVIDIA',
  productName: 'Quantum-2 QM9790',
  fabricType: 'InfiniBand',
  typicalRoles: ['leaf', 'spine'],
  ports: {
    totalPorts: 64,
    speed: {
      value: 400,
      unit: 'Gbps',
      displayName: '400G NDR',
    },
    portType: 'OSFP',
    supportsBreakout: true,
    breakoutOptions: ['1x400G', '2x200G', '4x100G'],
  },
  power: {
    typicalWatts: 640, // Passive cables (lower than managed)
    maxWatts: 1610, // Active cables
    idleWatts: 300,
    perPortWatts: 15,
    psuConfig: {
      count: 2,
      capacity: 2000,
      redundancy: 'N+1',
    },
  },
  physical: {
    rackUnits: 1,
    widthMm: 438,
    depthMm: 660,
    weightKg: 13.6, // Single PSU weight
    airflow: 'front-to-back',
    maxBtuPerHour: 5493,
  },
  performance: {
    switchingCapacityTbps: 25.6,
    forwardingRateMpps: 66500,
    latencyNs: 130,
    bufferSizeMB: 64,
  },
  features: {
    layer2: true,
    layer3: true,
    vxlan: false,
    evpn: false,
    rdma: true,
    adaptiveRouting: true,
    telemetry: true,
    inBandManagement: false, // Externally managed
    oobManagement: true,
    additionalFeatures: ['Externally managed via UFM', 'SHARP v3', 'Lower power than managed version'],
  },
  metadata: {
    productUrl: 'https://docs.nvidia.com/networking/display/qm97x0pub/introduction',
    releaseYear: 2022,
    endOfLife: false,
    notes: 'Unmanaged version for large fabrics with external UFM management. Lower power consumption.',
  },
};

/**
 * Quantum-2 QM9701 (DGX Mountable, 64-port NDR)
 */
export const QUANTUM_2_QM9701: SwitchModel = {
  id: 'nvidia-q2-qm9701',
  name: 'QM9701',
  vendor: 'NVIDIA',
  productName: 'Quantum-2 QM9701',
  fabricType: 'InfiniBand',
  typicalRoles: ['leaf'],
  ports: {
    totalPorts: 64,
    speed: {
      value: 400,
      unit: 'Gbps',
      displayName: '400G NDR',
    },
    portType: 'OSFP',
    supportsBreakout: true,
    breakoutOptions: ['1x400G', '2x200G', '4x100G'],
  },
  power: {
    typicalWatts: 720,
    maxWatts: 1660,
    idleWatts: 340,
    perPortWatts: 15,
    psuConfig: {
      count: 1,
      capacity: 1800,
      redundancy: 'N',
    },
  },
  physical: {
    rackUnits: 1,
    widthMm: 438,
    depthMm: 854, // Extended depth for DGX mounting
    weightKg: 16.88,
    airflow: 'front-to-back',
    maxBtuPerHour: 5662,
  },
  performance: {
    switchingCapacityTbps: 25.6,
    forwardingRateMpps: 66500,
    latencyNs: 130,
    bufferSizeMB: 64,
  },
  features: {
    layer2: true,
    layer3: true,
    vxlan: false,
    evpn: false,
    rdma: true,
    adaptiveRouting: true,
    telemetry: true,
    inBandManagement: true,
    oobManagement: true,
    additionalFeatures: ['DGX mountable design', 'SHARP v3', '40-59.5V DC power input'],
  },
  metadata: {
    productUrl: 'https://docs.nvidia.com/networking/display/qm97x0pub/introduction',
    releaseYear: 2022,
    endOfLife: false,
    notes: 'Extended depth (854mm) for direct DGX system mounting. DC power input (40-59.5V).',
  },
};

/**
 * Quantum HDR QM8700 (Managed, 40-port HDR)
 */
export const QUANTUM_HDR_QM8700: SwitchModel = {
  id: 'nvidia-hdr-qm8700',
  name: 'QM8700',
  vendor: 'NVIDIA',
  productName: 'Quantum HDR QM8700',
  fabricType: 'InfiniBand',
  typicalRoles: ['leaf', 'spine'],
  ports: {
    totalPorts: 40,
    speed: {
      value: 200,
      unit: 'Gbps',
      displayName: '200G HDR',
    },
    portType: 'QSFP56',
    supportsBreakout: true,
    breakoutOptions: ['1x200G', '2x100G HDR'],
  },
  power: {
    typicalWatts: 550, // Estimate based on similar switches
    maxWatts: 900, // Estimate
    idleWatts: 250,
    perPortWatts: 10,
    psuConfig: {
      count: 2,
      capacity: 1100,
      redundancy: 'N+1',
    },
  },
  physical: {
    rackUnits: 1,
    widthMm: 438,
    depthMm: 660,
    weightKg: 12,
    airflow: 'front-to-back',
    maxBtuPerHour: 3070,
  },
  performance: {
    switchingCapacityTbps: 16.0,
    forwardingRateMpps: 7200,
    latencyNs: 130,
    bufferSizeMB: 32,
  },
  features: {
    layer2: true,
    layer3: true,
    vxlan: false,
    evpn: false,
    rdma: true,
    adaptiveRouting: true,
    telemetry: true,
    inBandManagement: true,
    oobManagement: true,
    additionalFeatures: [
      'Onboard subnet manager (up to 2,048 nodes)',
      'x86 dual-core processor',
      'SHARP v2',
    ],
  },
  metadata: {
    productUrl: 'https://docs.nvidia.com/networking/display/qm87xx/specifications',
    releaseYear: 2020,
    endOfLife: false,
    notes: 'Legacy generation for H100 deployments. 40 QSFP56 connectors.',
  },
};

/**
 * Quantum HDR QM8790 (Unmanaged, 40-port HDR)
 */
export const QUANTUM_HDR_QM8790: SwitchModel = {
  id: 'nvidia-hdr-qm8790',
  name: 'QM8790',
  vendor: 'NVIDIA',
  productName: 'Quantum HDR QM8790',
  fabricType: 'InfiniBand',
  typicalRoles: ['leaf', 'spine'],
  ports: {
    totalPorts: 40,
    speed: {
      value: 200,
      unit: 'Gbps',
      displayName: '200G HDR',
    },
    portType: 'QSFP56',
    supportsBreakout: true,
    breakoutOptions: ['1x200G', '2x100G'],
  },
  power: {
    typicalWatts: 500,
    maxWatts: 850,
    idleWatts: 230,
    perPortWatts: 10,
    psuConfig: {
      count: 2,
      capacity: 1100,
      redundancy: 'N+1',
    },
  },
  physical: {
    rackUnits: 1,
    widthMm: 438,
    depthMm: 660,
    weightKg: 11,
    airflow: 'front-to-back',
    maxBtuPerHour: 2900,
  },
  performance: {
    switchingCapacityTbps: 16.0,
    forwardingRateMpps: 7200,
    latencyNs: 130,
    bufferSizeMB: 32,
  },
  features: {
    layer2: true,
    layer3: true,
    vxlan: false,
    evpn: false,
    rdma: true,
    adaptiveRouting: true,
    telemetry: true,
    inBandManagement: false,
    oobManagement: true,
    additionalFeatures: ['Externally managed via UFM', 'SHARP v2'],
  },
  metadata: {
    productUrl: 'https://docs.nvidia.com/networking/display/qm87xx/specifications',
    releaseYear: 2020,
    endOfLife: false,
    notes: 'Unmanaged version requiring external UFM platform for management.',
  },
};

/**
 * All NVIDIA InfiniBand switches
 */
export const NVIDIA_INFINIBAND_SWITCHES: readonly SwitchModel[] = [
  QUANTUM_X800_Q3400,
  QUANTUM_X800_Q3200,
  QUANTUM_X800_Q3450,
  QUANTUM_2_QM9700,
  QUANTUM_2_QM9790,
  QUANTUM_2_QM9701,
  QUANTUM_HDR_QM8700,
  QUANTUM_HDR_QM8790,
] as const;

/**
 * Get NVIDIA InfiniBand switch by ID
 */
export function getNvidiaInfiniBandSwitch(id: string): SwitchModel | undefined {
  return NVIDIA_INFINIBAND_SWITCHES.find((sw) => sw.id === id);
}

/**
 * Get NVIDIA InfiniBand switches by speed
 */
export function getNvidiaInfiniBandSwitchesBySpeed(speedGbps: number): readonly SwitchModel[] {
  return NVIDIA_INFINIBAND_SWITCHES.filter((sw) => sw.ports.speed.value === speedGbps);
}

/**
 * Get recommended NVIDIA InfiniBand switch for scale
 */
export function getRecommendedNvidiaInfiniBandSwitch(
  nodeCount: number,
  speedGbps: number = 400
): SwitchModel {
  // XDR (800G) for B200/GB200
  if (speedGbps >= 800) {
    return nodeCount > 128 ? QUANTUM_X800_Q3400 : QUANTUM_X800_Q3200;
  }

  // NDR (400G) for H100/H200
  if (speedGbps >= 400) {
    if (nodeCount <= 32) {
      return QUANTUM_2_QM9700; // Managed for smaller deployments
    }
    return QUANTUM_2_QM9790; // Unmanaged for larger deployments
  }

  // HDR (200G) legacy
  return QUANTUM_HDR_QM8700;
}
